import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { APP_VERSION, BUILD_DATE } from './version';
import Login from './components/Auth/Login';
import CurrenciesList from './components/Dictionaries/CurrenciesList';
import TimezonesList from './components/Dictionaries/TimezonesList';
import CountriesList from './components/Dictionaries/CountriesList';
import AirportsList from './components/Dictionaries/AirportsList';
import AirportPairsList from './components/Dictionaries/AirportPairsList';
import RoutesList from './components/Dictionaries/RoutesList';
import CarriersList from './components/Dictionaries/CarriersList';
import CarrierCodesList from './components/Dictionaries/CarrierCodesList';
import AgentsList from './components/Dictionaries/AgentsList';
import PlanesList from './components/Dictionaries/PlanesList';
import PlaneModelsList from './components/Dictionaries/PlaneModelsList';
import FlightsList from './components/Flights/FlightsList';
import LegsScheduleList from './components/Flights/LegsScheduleList';
import PassengersList from './components/Passengers/PassengersList';
import PassengerDocumentsList from './components/Passengers/PassengerDocumentsList';
import BookingsList from './components/Bookings/BookingsList';
import TicketsList from './components/Bookings/TicketsList';
import TicketSegmentsList from './components/Bookings/TicketSegmentsList';
import PaymentsList from './components/Bookings/PaymentsList';
import FlightsStatList from './components/Statistics/FlightsStatList';
import FlightsSummary from './components/Statistics/FlightsSummary';
import FlightsMap from './components/Statistics/FlightsMap';
import MyFlights from './components/Flights/MyFlights';
import MyBookings from './components/Bookings/MyBookings';
import BookingDetails from './components/Bookings/BookingDetails';
import ChangePassword from './components/Auth/ChangePassword';
import UsersList from './components/Admin/UsersList';
import BlockedAttemptsList from './components/Admin/BlockedAttemptsList';
import ShareLinksList from './components/Account/ShareLinksList';
import SessionsList from './components/Account/SessionsList';
import SharePage from './components/SharePage';
import { clearAuth, getStoredAuth, storeAuth } from './utils/authStorage';
import { getBlockedAttemptCounts, getSessions, logout } from './services/api';

function App() {
  const storedAuth = getStoredAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(!!storedAuth.token);
  const [isAdmin, setIsAdmin] = useState(storedAuth.isAdmin);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [blockedAttemptCounts, setBlockedAttemptCounts] = useState({ ipCount: 0, loginCount: 0 });

  const handleLogin = (token, userId, admin, rememberMe) => {
    storeAuth({ token, userId, isAdmin: admin, rememberMe });
    setIsAuthenticated(true);
    setIsAdmin(!!admin);
  };

  const handleLocalLogout = () => {
    clearAuth();
    setIsAuthenticated(false);
    setIsAdmin(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Local credentials must be cleared even if the server is unreachable.
    }
    handleLocalLogout();
  };

  useEffect(() => {
    if (!storedAuth.token) return;

    getSessions().catch(() => {
      clearAuth();
      setIsAuthenticated(false);
      setIsAdmin(false);
    });
  }, [storedAuth.token]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      setBlockedAttemptCounts({ ipCount: 0, loginCount: 0 });
      return undefined;
    }

    let isCurrent = true;
    const loadBlockedAttemptCounts = async () => {
      try {
        const response = await getBlockedAttemptCounts();
        if (isCurrent) setBlockedAttemptCounts(response.data);
      } catch (error) {
        if (isCurrent) setBlockedAttemptCounts({ ipCount: 0, loginCount: 0 });
      }
    };

    loadBlockedAttemptCounts();
    const refreshInterval = setInterval(loadBlockedAttemptCounts, 30000);
    return () => {
      isCurrent = false;
      clearInterval(refreshInterval);
    };
  }, [isAuthenticated, isAdmin]);

  return (
    <Router>
      <Routes>
        <Route path="/share/:uid" element={<SharePage />} />
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />}
        />

        <Route element={<RequireAuth isAuthenticated={isAuthenticated} />}>
          <Route
            element={
              <AuthenticatedLayout
                onLogout={handleLogout}
                isAdmin={isAdmin}
                blockedAttemptCounts={blockedAttemptCounts}
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
              />
            }
          >
            <Route path="/" element={<MyFlights />} />
            <Route path="/my-bookings" element={<MyBookings />} />

            <Route path="/currencies" element={<CurrenciesList />} />
            <Route path="/timezones" element={<TimezonesList />} />
            <Route path="/countries" element={<CountriesList />} />
            <Route path="/airports" element={<AirportsList />} />
            <Route path="/airport-pairs" element={<AirportPairsList />} />
            <Route path="/routes" element={<RoutesList />} />
            <Route path="/carriers" element={<CarriersList />} />
            <Route path="/carrier-codes" element={<CarrierCodesList />} />
            <Route path="/agents" element={<AgentsList />} />
            <Route path="/planes" element={<PlanesList />} />
            <Route path="/plane-models" element={<PlaneModelsList />} />

            <Route path="/flights" element={<FlightsList />} />
            <Route path="/legs-schedule" element={<LegsScheduleList />} />

            <Route path="/passengers" element={<PassengersList />} />
            <Route path="/passenger-documents" element={<PassengerDocumentsList />} />

            <Route path="/bookings" element={<BookingsList />} />
            <Route path="/booking-details/:id" element={<BookingDetails />} />
            <Route path="/tickets" element={<TicketsList />} />
            <Route path="/ticket-segments" element={<TicketSegmentsList />} />
            <Route path="/payments" element={<PaymentsList />} />

            <Route path="/flights-map" element={<FlightsMap />} />
            <Route path="/flights-summary" element={<FlightsSummary />} />
            <Route path="/flights-stat" element={<FlightsStatList />} />

            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/sessions" element={<SessionsList onCurrentSessionEnded={handleLocalLogout} />} />
            <Route path="/share-links" element={<ShareLinksList />} />

            {isAdmin && <Route path="/users" element={<UsersList />} />}
            {isAdmin && <Route path="/blocked-ips" element={<BlockedAttemptsList type="ip" />} />}
            {isAdmin && <Route path="/blocked-logins" element={<BlockedAttemptsList type="login" />} />}
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

function RequireAuth({ isAuthenticated }) {
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function AuthenticatedLayout({ onLogout, isAdmin, blockedAttemptCounts, isSidebarOpen, onToggleSidebar }) {
  return (
    <div className={`app ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Sidebar
        onLogout={onLogout}
        isAdmin={isAdmin}
        blockedAttemptCounts={blockedAttemptCounts}
        version={APP_VERSION}
        buildDate={BUILD_DATE}
        isOpen={isSidebarOpen}
        onToggle={onToggleSidebar}
      />
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}

function NotFound() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        textAlign: 'center',
        gap: '12px',
      }}
    >
      <h1 style={{ margin: 0 }}>404</h1>
      <p style={{ margin: 0 }}>Page not found.</p>
    </div>
  );
}

function Sidebar({ onLogout, isAdmin, blockedAttemptCounts, version, buildDate, isOpen, onToggle }) {
  const location = useLocation();

  const isActive = (path) => (location.pathname === path ? 'active' : '');

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <button
        className="sidebar-toggle"
        onClick={onToggle}
        aria-label={isOpen ? 'Hide menu' : 'Show menu'}
      >
        {isOpen ? '⟨' : '⟩'}
      </button>
      <div className="sidebar-body">
        <div>
          <h1>FLDB</h1>
          <nav>
            <Link to="/" className={isActive('/')}>My Flights</Link>
            <Link to="/my-bookings" className={isActive('/my-bookings')}>My Bookings</Link>

            <h3>Dictionaries</h3>
            <Link to="/currencies" className={isActive('/currencies')}>Currencies</Link>
            <Link to="/timezones" className={isActive('/timezones')}>Timezones</Link>
            <Link to="/countries" className={isActive('/countries')}>Countries</Link>
            <Link to="/airports" className={isActive('/airports')}>Airports</Link>
            <Link to="/airport-pairs" className={isActive('/airport-pairs')}>Airport Pairs</Link>
            <Link to="/routes" className={isActive('/routes')}>Routes</Link>
            <Link to="/carriers" className={isActive('/carriers')}>Carriers</Link>
            <Link to="/carrier-codes" className={isActive('/carrier-codes')}>Carrier Codes</Link>
            <Link to="/agents" className={isActive('/agents')}>Agents</Link>
            <Link to="/planes" className={isActive('/planes')}>Planes</Link>
            <Link to="/plane-models" className={isActive('/plane-models')}>Plane Models</Link>

            <h3>Flights</h3>
            <Link to="/flights" className={isActive('/flights')}>Flights</Link>
            <Link to="/legs-schedule" className={isActive('/legs-schedule')}>Legs Schedule</Link>

            <h3>Passengers</h3>
            <Link to="/passengers" className={isActive('/passengers')}>Passengers</Link>
            <Link to="/passenger-documents" className={isActive('/passenger-documents')}>Documents</Link>

            <h3>Bookings</h3>
            <Link to="/bookings" className={isActive('/bookings')}>Bookings</Link>
            <Link to="/tickets" className={isActive('/tickets')}>Tickets</Link>
            <Link to="/ticket-segments" className={isActive('/ticket-segments')}>Segments</Link>
            <Link to="/payments" className={isActive('/payments')}>Payments</Link>

            <h3>Statistics</h3>
            <Link to="/flights-map" className={isActive('/flights-map')}>Flights Map</Link>
            <Link to="/flights-summary" className={isActive('/flights-summary')}>Flights Summary</Link>
            <Link to="/flights-stat" className={isActive('/flights-stat')}>Flights List</Link>

            {isAdmin && (
              <>
                <h3>Admin</h3>
                <Link to="/users" className={isActive('/users')}>Users</Link>
                <Link to="/blocked-ips" className={isActive('/blocked-ips')}>
                  Blocked IPs ({blockedAttemptCounts.ipCount})
                </Link>
                <Link to="/blocked-logins" className={isActive('/blocked-logins')}>
                  Blocked logins ({blockedAttemptCounts.loginCount})
                </Link>
              </>
            )}

            <h3>Account</h3>
            <Link to="/change-password" className={isActive('/change-password')}>Change Password</Link>
            <Link to="/sessions" className={isActive('/sessions')}>Sessions</Link>
            <Link to="/share-links" className={isActive('/share-links')}>Share Links</Link>
            <button
              type="button"
              className="sidebar-link-button"
              onClick={onLogout}
            >
              Logout
            </button>
          </nav>
        </div>
        <div
          style={{
            paddingBottom: '15px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            paddingTop: '15px',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          <div style={{ fontSize: '0.85em' }}>v{version}</div>
          <div style={{ fontSize: '0.75em', marginTop: '4px' }}>{buildDate}</div>
        </div>
      </div>
    </aside>
  );
}

export default App;
